// Pure helpers for the BulkImportRoute. Ports the legacy
// frontend/app.jsx's column-mapping + cell-coercion logic verbatim
// so the backend's Pydantic validators see the same inputs.

export const ALL_PROJECT_FIELDS = [
  "project_id",
  "airport_code",
  "customer_code",
  "customer_business_unit",
  "address",
  "landlord",
  "primary_epc",
  "backup_epc",
  "operation_type",
  "location_type",
  "ownership_type",
  "lease_expiry",
  "remaining_lease_years",
  "target_system_size_kwp",
  "existing_pv_summary",
  "has_battery",
  "mpan",
  "dno",
  "idno",
  "comments",
  "shovel_readiness",
  "on_site_survey",
  "roof_condition",
  "structural",
  "landlord_consent",
  "epc_cost",
  "roof_warranty",
  "mfl_exclusion",
] as const;

export type ProjectField = (typeof ALL_PROJECT_FIELDS)[number];

export const STATUS_NOTE_FIELDS: ReadonlySet<ProjectField> = new Set([
  "shovel_readiness",
  "on_site_survey",
  "roof_condition",
  "structural",
  "landlord_consent",
  "epc_cost",
  "roof_warranty",
  "mfl_exclusion",
]);

const FIELD_SYNONYMS: Record<ProjectField, string[]> = {
  project_id: [
    "project_id", "project id", "id", "site id", "site_id",
    "project", "building id", "site",
  ],
  customer_code: ["customer_code", "customer code", "customer"],
  customer_business_unit: [
    "customer_business_unit", "business unit", "bu", "amzl/cf", "amzl",
  ],
  airport_code: ["airport_code", "airport code", "airport"],
  address: ["address", "site address", "location", "site location"],
  landlord: ["landlord", "landlord name", "landlord_name", "ll"],
  primary_epc: ["primary_epc", "primary epc", "epc", "epc - primary"],
  backup_epc: ["backup_epc", "backup epc", "epc - backup"],
  operation_type: ["operation_type", "operation type", "operation"],
  location_type: ["location_type", "location type"],
  ownership_type: ["ownership_type", "ownership", "ownership type"],
  lease_expiry: ["lease_expiry", "lease expiry", "lease end"],
  remaining_lease_years: [
    "remaining_lease_years", "remaining lease years",
    "remaining lease", "lease remaining",
  ],
  target_system_size_kwp: [
    "target_system_size_kwp", "system size kwp", "system size",
    "kwp", "latest size", "size",
  ],
  existing_pv_summary: [
    "existing_pv_summary", "existing pv", "existing pv summary",
    "existing",
  ],
  has_battery: ["has_battery", "battery", "has battery"],
  mpan: ["mpan", "mpans"],
  dno: ["dno", "dno operator"],
  idno: ["idno", "idno operator"],
  comments: ["comments", "comment", "notes", "roof", "site contacts"],
  shovel_readiness: ["shovel readiness", "shovel_readiness"],
  on_site_survey: ["on-site survey", "on site survey"],
  roof_condition: ["roof condition"],
  structural: ["structural"],
  landlord_consent: ["ll consent", "landlord consent"],
  epc_cost: ["epc cost"],
  roof_warranty: ["roof warranty"],
  mfl_exclusion: ["mfl/exclusion", "mfl exclusion", "mfl"],
};

// Pair of headers that together unambiguously identify the Amazon
// "Building tracking" workbook - when present we auto-map
// project_id from airport_code rather than the generic guesser.
export const AMAZON_TRACKER_HEADERS = ["shovel readiness", "airport code"];

export const _norm = (s: unknown): string =>
  String(s ?? "").toLowerCase().replace(/[_\-\s]+/g, " ").trim();

export const mapHeaderToField = (header: string): ProjectField | null => {
  const norm = _norm(header);
  for (const [field, synonyms] of Object.entries(FIELD_SYNONYMS) as [
    ProjectField,
    string[],
  ][]) {
    if (synonyms.some((s) => _norm(s) === norm)) return field;
  }
  return null;
};

export const detectAmazonTracker = (headers: string[]): boolean => {
  const set = new Set(headers.map(_norm));
  return AMAZON_TRACKER_HEADERS.every((h) => set.has(_norm(h)));
};

// Closed-enum normalisers - Project's Pydantic validators reject
// anything that isn't an exact-string match, so we normalise at the
// boundary rather than relax the schema.
const normaliseOperationType = (raw: unknown): string | undefined => {
  const s = String(raw ?? "").toLowerCase().trim();
  if (!s) return undefined;
  if (s.includes("ar sortable") || s.includes("sortable")) return "AR Sortable";
  if (s.includes("delivery station")) return "Delivery Station";
  if (s.includes("build to suit") || s.includes("bts")) return "Build to Suit";
  if (s.includes("existing")) return "Existing";
  return "Other";
};

const normaliseOwnershipType = (raw: unknown): string | undefined => {
  const s = String(raw ?? "").toLowerCase().trim();
  if (!s) return undefined;
  if (s.includes("leas")) return "Leased";
  if (s.includes("owner") && s.includes("occup")) return "Owner Occupier";
  if (s === "oo") return "Owner Occupier";
  return "Other";
};

export const coerceCell = (
  field: ProjectField,
  value: unknown,
): string | number | boolean | undefined => {
  if (value == null || value === "") return undefined;
  if (field === "target_system_size_kwp" || field === "remaining_lease_years") {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }
  if (field === "has_battery") {
    const s = String(value).toLowerCase().trim();
    if (["yes", "y", "true", "1", "x", "✓"].includes(s)) return true;
    if (["no", "n", "false", "0", ""].includes(s)) return false;
    return undefined;
  }
  if (field === "customer_code") {
    const s = String(value).trim().toUpperCase();
    return ["AMZ", "OTHER"].includes(s) ? s : "OTHER";
  }
  if (field === "operation_type") return normaliseOperationType(value);
  if (field === "ownership_type") return normaliseOwnershipType(value);
  return String(value).trim();
};

// Take the auto-mapping + the raw grid rows and build the array of
// `Project`-shaped dicts to POST. Mirrors the legacy
// `projectsToImport` derivation exactly.
export interface BulkRow {
  project_id: string;
  // Plus arbitrary other fields; the backend `Project` schema does
  // its own validation. We keep this loose because the FE has no
  // reason to mirror every Pydantic field.
  [key: string]: unknown;
}

export const buildProjectsToImport = (
  rows: unknown[][],
  mapping: Record<number, ProjectField>,
  trackerType: "amazon" | "generic" | null,
  fileName: string | null,
): BulkRow[] => {
  return rows
    .map((row, idx) => {
      const proj: Record<string, unknown> = {};
      const statusNotes: Record<string, { summary: string }> = {};
      for (const [colIdxStr, field] of Object.entries(mapping) as [
        string,
        ProjectField,
      ][]) {
        const v = coerceCell(field, row[Number(colIdxStr)]);
        if (v === undefined) continue;
        if (STATUS_NOTE_FIELDS.has(field)) {
          statusNotes[field] = { summary: String(v) };
        } else {
          proj[field] = v;
        }
      }
      Object.assign(proj, statusNotes);

      if (!proj.project_id) {
        if (proj.airport_code) proj.project_id = proj.airport_code;
        else if (proj.address)
          proj.project_id = String(proj.address).slice(0, 120);
        else proj.project_id = `IMPORT#row-${idx + 1}`;
      }

      if (trackerType === "amazon" && !proj.customer_code) {
        proj.customer_code = "AMZ";
      }

      proj.last_synced_source = fileName
        ? `bulk-import:${fileName}`
        : "bulk-import";

      return proj as BulkRow;
    })
    .filter((p) => {
      if (!p.project_id) return false;
      const meaningfulKeys = Object.keys(p).filter(
        (k) => k !== "project_id" && k !== "last_synced_source",
      );
      return meaningfulKeys.length > 0;
    });
};
